import { DATE_TIME_FORMAT } from "constants"
import dayjs from "dayjs"
import taskStatusTransformer from "features/leads/api/taskStatusTransformer"
import parentApi from "store/parentApi"
import { convertQueryObjToString, localisedValue } from "utils"
import { dateTimeFunction } from "utils/date"
import clientAnswerTransformer from "./clientAnswerTransformer"
import clientQuestionTransformer from "./clientQuestionTransformer"
import clientProfileFilterOptionsTransformer from "./clientProfileFilterOptionsTransformer"
import editedTaskLogTransformer from "./editedtaskLogTransformer"
import editedTodoLogTransformer from "./editedTodoLogTransformer"
import getTaskTypeCategoriesTransformHelper from "./getTaskTypeCategoriesTransformHelper"
import getTodosCountTransformer from "./getTodosCountTransformer"
import taskListingTransformer from "./taskListingTransformer"
import { YYYY_MM_DD_FORMAT } from "constants"
import { HH_MM_SS_FORMAT } from "constants"
import transformTaskVirtualMeetingForDrawer from "./taskVirtualMeetingTransformer"

const taskFormApi = parentApi.injectEndpoints({
  endpoints: build => ({
    getTaskTypeCategories: build.query({
      query: params => `taskTypes/hierarchy?${convertQueryObjToString(params)}`,
      transformResponse: response => {
        const transformedData = getTaskTypeCategoriesTransformHelper(response)
        return transformedData
      },
    }),
    getCellNumber: build.query({
      query: clientId => `otps/cells?clientId=${clientId}`,
    }),

    addTask: build.mutation({
      query: ({ lead, encodedFile, verifyMeeting, ...values }) => {
        values["completionDate"] = dayjs(
          values?.completionDate?.format(YYYY_MM_DD_FORMAT) +
            " " +
            (!!values?.meetingTime
              ? values?.meetingTime?.[1]
                ? values?.meetingTime?.[1]?.format(HH_MM_SS_FORMAT)
                : ""
              : values?.completionTime?.[0]
                ? values?.completionTime?.[0]?.format(HH_MM_SS_FORMAT)
                : "")
        )
        values?.meetingTime &&
          (values["meetingTime"] = [
            dayjs(
              values?.completionDate?.format(YYYY_MM_DD_FORMAT) +
                " " +
                (values?.meetingTime?.[0] ? values?.meetingTime?.[0].format(HH_MM_SS_FORMAT) : "")
            ),
            dayjs(
              values?.completionDate?.format(YYYY_MM_DD_FORMAT) +
                " " +
                (values?.meetingTime?.[1] ? values?.meetingTime?.[1].format(HH_MM_SS_FORMAT) : "")
            ),
          ])
        values["deadLine"] = dayjs(
          values?.deadLine?.format(YYYY_MM_DD_FORMAT) +
            " " +
            (values?.deadLineTime?.[0] ? values?.deadLineTime?.[0].format(HH_MM_SS_FORMAT) : "")
        )
        if (values?.timeSlot) {
          const start = dayjs(
            values?.dateSlot?.format(YYYY_MM_DD_FORMAT) + " " + values?.timeSlot?.[0].format(HH_MM_SS_FORMAT)
          )
          const end = dayjs(
            values?.dateSlot?.format(YYYY_MM_DD_FORMAT) + " " + values?.timeSlot?.[1].format(HH_MM_SS_FORMAT)
          )
          values["timeSlot"] = [start, end]
        }
        if (values?.newMeetingTime) {
          const start = dayjs(
            values?.newMeetingDate?.format(YYYY_MM_DD_FORMAT) +
              " " +
              values?.newMeetingTime?.[0].format(HH_MM_SS_FORMAT)
          )
          const end = dayjs(
            values?.newMeetingDate?.format(YYYY_MM_DD_FORMAT) +
              " " +
              values?.newMeetingTime?.[1].format(HH_MM_SS_FORMAT)
          )
          values["newMeetingTime"] = [start, end]
        }
        let verificationStatus = encodedFile?.["0"]?.status === "rejected" ? false : true
        const payload = {
          ...(!!values.voiceComment && {
            commentVoiceRecording: {
              data: values?.voiceComment?.split(",")[1],
              name: "voice-recording.webm",
              type: "audio/webm",
            },
          }),
          currentStatus: values?.subLocation
            ? values.subLocation
            : values?.subofSubTask
              ? values.subofSubTask
              : values.subTask,
          ...(values?.nextTask !== "do_nothing" && { nextStatus: values?.nextTask }),
          status: "successful",
          projectId: values?.project?.projectId ? +values?.project?.projectId : +lead?.Project?.projectId,
          classificationId: values?.leadsClassification ? +values?.leadsClassification : +values?.tsrClassification,
          tsrClassificationId: values?.tsrClassification ? +values?.tsrClassification : +values?.leadsClassification,
          verified: verificationStatus,
          ...(encodedFile && {
            attachments: encodedFile
              ? encodedFile?.map(e => {
                  return { file: e?.file?.split(",")[1], name: e?.name, trackingId: e?.trackingId }
                })
              : null,
          }),
          ...(values?.deadLine && {
            nextStatusDeadline: dateTimeFunction({
              date: values?.timeSlot
                ? dayjs(values?.timeSlot[0]).format(DATE_TIME_FORMAT)?.toString()
                : dayjs(values?.deadLine).format(DATE_TIME_FORMAT),
              timeZone: true,
              apiRequest: true,
            }),
          }),
          completionDate: dateTimeFunction({
            date: values?.completionDate,
            timeZone: true,
            apiRequest: true,
          }),
          comments: values.comment,
          ...(values?.timeSlot && {
            calendarEvent: {
              date: dateTimeFunction({ date: values?.dateSlot, timeZone: true, apiRequest: true })?.toString(),
              start: dateTimeFunction({ date: values.timeSlot[0], timeZone: true, apiRequest: true })?.toString(),
              end: dateTimeFunction({ date: values.timeSlot[1], timeZone: true, apiRequest: true })?.toString(),
            },
          }),
          ...(values?.newMeetingDate &&
            values?.newMeetingTime && {
              calendarEvent: {
                date: dateTimeFunction({ date: values?.newMeetingDate, timeZone: true, apiRequest: true })?.toString(),
                start: dateTimeFunction({
                  date: values.newMeetingTime[0],
                  timeZone: true,
                  apiRequest: true,
                })?.toString(),
                end: dateTimeFunction({ date: values.newMeetingTime[1], timeZone: true, apiRequest: true })?.toString(),
              },
            }),
          ...(values?.salesEvent && { salesEventId: values?.salesEvent }),
          ...(values?.meetingTime && { startTime: values?.meetingTime[0], endTime: values?.meetingTime[1] }),
          ...(values?.companyOffice && { agencyOfficeId: values?.companyOffice }),
          ...(values?.plusCode && { localityPlusCode: values?.plusCode }),
          ...(values?.visitedProject && { taskProjectId: values?.visitedProject }),
          ...(values?.googleMeeting && {
            googleVerified: values?.googleVerified,
            virtualDetails: {
              provider: "google_meet",
              conferenceRecordName: values?.googleMeeting?.conferenceRecordName,
              externalEventId: values?.googleMeeting?.externalEventId,
            },
          }),
          source: "web_v2",
        }
        return { url: `/internalInquiries/${lead?.id}/task/v2`, method: "POST", body: payload }
      },
      invalidatesTags: (result, error, args) => {
        return result ? [{ type: "leads", id: args.lead.Client.id }, "taskLogs", "client-detail", "leads", "todos"] : []
      },
    }),
    createOtp: build.mutation({
      query: ({ selectedNumber, lead }) => {
        const payload = { cell: selectedNumber, clientId: lead?.clientId, inquiryId: lead?.id, userId: lead?.User?.id }
        return { url: "otps/generate", method: "POST", body: payload }
      },
    }),
    verifyOtp: build.mutation({
      query: ({ otpNumber, lead }) => {
        const payload = { otp: otpNumber, clientId: lead?.clientId, inquiryId: lead?.id, userId: lead?.User?.id }
        return { url: "otps/verify", method: "PUT", body: payload }
      },
    }),

    getClientQuestions: build.query({
      query: id => `clientProfileQuestions?clientId=${id}`,
      transformResponse: response => {
        const transformedData = clientQuestionTransformer(response?.data)
        return transformedData
      },
    }),
    getClientProfileFilterOptions: build.query({
      query: filterKey => `clientProfileQuestions?filter=${filterKey}`,
      transformResponse: (response, meta, arg) => {
        const transformedData = clientProfileFilterOptionsTransformer(response, { arg })
        return transformedData
      },
    }),
    getClientAnswer: build.query({
      query: id => `clients/attemptedAnswers/${id}`,
      transformResponse: response => {
        const transformedData = clientAnswerTransformer(response)
        return transformedData
      },
    }),

    getTaskListing: build.query({
      query: data => {
        let params = { ...data }
        const checkDate = key => {
          if (!!params[key]) {
            const dateR = params[key].split(",")
            params[key] = encodeURIComponent(`{"from":"${dateR[0]}","to":"${dateR[1]}"}`)
          }
        }
        const checkUsers = key => {
          if (!!params[key]) {
            let childIdentifierKey = key + "_withoutChildren"
            params[key] = encodeURIComponent(
              `{"userIds":[${decodeURIComponent(params[key])}],"includeAllChildren":${!params?.[childIdentifierKey]}}`
            )
          }
        }
        checkDate("taskAdded")
        checkUsers("assignedTo")
        return `/tasks/search?${convertQueryObjToString(params)}`
      },
      transformResponse: data => taskListingTransformer(data),
      providesTags: ["tasksListing"],
    }),
    getTaskTypes: build.query({
      query: params => `/taskTypes?taskTypeFor=lead&hierarchy=true&${convertQueryObjToString(params)}`,
      transformResponse: data => taskStatusTransformer(data),
    }),

    getTaskVirtualMeeting: build.query({
      query: taskId => `/tasks/${taskId}/virtual-meeting`,
      transformResponse: response => transformTaskVirtualMeetingForDrawer(response?.data ?? response),
    }),
    getTaskVerificationStatus: build.query({
      query: taskId => `/tasks/verificationStatus/${taskId}`,
      transformResponse: response => response?.data ?? response,
    }),

    addAnswers: build.mutation({
      query: ({ values, client }) => {
        const sliderToRange = arr => ({
          from: +(arr?.[0] ?? 0),
          to: +(arr?.[1] ?? 0),
        })

        const payload = {
          // Personal Information
          ...(!!values?.gender && { gender: values?.gender }),
          ...(!!values?.nationalityId && { nationalityId: +values?.nationalityId }),
          ...(!!values?.maritalStatus && { maritalStatus: values?.maritalStatus }),
          ...(values?.children != null &&
            values?.children !== "" && { children: values?.children === "true" || values?.children === true }),
          ...(!!values?.relationshipStrength && { relationshipStrength: values?.relationshipStrength }),
          ...(!!values?.taxResidency && { taxResidency: values?.taxResidency }),

          // Financial & Professional Details
          ...(!!values?.occupation && { occupation: values?.occupation }),
          ...(!!values?.occupationOtherText && { occupationOtherText: values?.occupationOtherText }),
          ...(!!values?.teamSize && { teamSize: values?.teamSize }),
          ...(!!values?.industryId && { industryId: +values?.industryId }),
          ...(!!values?.netWorth && { netWorth: sliderToRange(values.netWorth) }),
          ...(!!values?.companyNameProfiling && { companyNameProfiling: values?.companyNameProfiling }),
          ...(!!values?.annualRevenueAUM && { annualRevenueAUM: sliderToRange(values.annualRevenueAUM) }),

          ...(!!values?.clientRatingId && { clientRatingId: +values?.clientRatingId }),
          ...(!!values?.keyShareholderUBO && { keyShareholderUBO: values?.keyShareholderUBO }),

          ...(!!values?.budget && { budget: +(values?.budget?.[1] ?? 0) }),
          ...(!!values?.interestedTypeId && { interestedTypeId: values?.interestedTypeId }),
          ...(!!values?.area && { area: values?.area }),
          ...(!!values?.preferredCountryId && { preferredCountryId: +values?.preferredCountryId }),
          ...(!!values?.preferredCityIds?.length && {
            preferredCityIds: values?.preferredCityIds?.map(id => +id),
          }),
          ...(!!values?.preferredCityRegion && { preferredCityRegion: values?.preferredCityRegion }),
          ...(!!values?.hasExistingProperty && {
            hasExistingProperty: values?.hasExistingProperty === "yes" ? true : false,
          }),
          ...(!!values?.preferredLocalityId && { preferredLocalityId: +values?.preferredLocalityId }),
          ...(!!values?.intent && { intent: values?.intent }),
          ...(!!values?.buyingIntentId && { buyingIntentId: +values?.buyingIntentId }),
          ...(!!values?.preferredFinancing && { preferredFinancing: values?.preferredFinancing }),

          // Market Exposure
          ...(!!values?.politicalExposure && { politicalExposure: values?.politicalExposure }),
          ...(!!values?.realEstateIndustryExposure && {
            realEstateIndustryExposure: values?.realEstateIndustryExposure,
          }),
          ...(!!values?.marketExposure && { marketExposure: values?.marketExposure }),
          ...(!!values?.socialCapital && { socialCapital: values?.socialCapital }),

          // Investment Strategy
          ...(!!values?.investmentStrategy && { investmentStrategy: values?.investmentStrategy }),
          ...(!!values?.investmentPeriod && { investmentPeriod: values?.investmentPeriod }),
          ...(!!values?.targetAnnualIncomePercent && {
            targetAnnualIncomePercent: values?.targetAnnualIncomePercent?.toString() ?? "",
          }),
          ...(!!values?.targetCapitalReturnPercent && {
            targetCapitalReturnPercent: values?.targetCapitalReturnPercent?.toString() ?? "",
          }),
          ...(!!values?.targetAllocation && { targetAllocation: sliderToRange(values.targetAllocation) }),

          ...(!!values?.capitalStructure && { capitalStructure: values?.capitalStructure }),
          ...(!!values?.assetClass && { assetClass: values?.assetClass }),
          ...(!!values?.riskProfile && { riskProfile: values?.riskProfile }),
          ...(!!values?.currentInvestmentPortfolio?.length && {
            currentInvestmentPortfolio: values?.currentInvestmentPortfolio,
          }),

          // Services Required
          ...(!!values?.shortTermLease && { shortTermLease: values?.shortTermLease }),
          ...(!!values?.longTermLease && { longTermLease: values?.longTermLease }),
          ...(!!values?.resaleFlipping && { resaleFlipping: values?.resaleFlipping }),
          ...(!!values?.residencyRequirement && { residencyRequirement: values?.residencyRequirement }),
          ...(!!values?.propertyManagement && { propertyManagement: values?.propertyManagement }),
          ...(!!values?.legalTaxServices && { legalTaxServices: values?.legalTaxServices }),
          ...(!!values?.travelLogisticsServices && { travelLogisticsServices: values?.travelLogisticsServices }),
        }
        return { url: `/clients/${client?.id}/answers`, method: "PUT", body: payload }
      },
      invalidatesTags: ["client-detail"],
    }),
    updateTask: build.mutation({
      query: ({ taskId, isEditingTSRTask, lead, leadDetail, encodedFile, ...values }) => {
        const payload = isEditingTSRTask
          ? {
              currentStatus: values?.subLocation
                ? values.subLocation
                : values?.subofSubTask
                  ? values?.subofSubTask
                  : values?.subTask,
              nextStatus: values?.nextTask,
              phoneCallId: values?.call,
              status: "successful",
              projectId: values?.project ? +values?.project : +lead?.Project?.projectId,
              tsrClassificationId: values.tsrClassification ? +values.tsrClassification : +values.leadsClassification,
              classificationId: +leadDetail?.inquiry?.InternalInquiryClassification?.id,
              completionDate: dateTimeFunction({
                date: values?.completionDate,
                timeZone: true,
                apiRequest: true,
              }),
              nextStatusDeadline: dateTimeFunction({
                date: values.deadLine.format(DATE_TIME_FORMAT),
                timeZone: true,
                apiRequest: true,
              }),
              ...(encodedFile && {
                attachments: encodedFile
                  ? encodedFile.map(e => {
                      return { file: e.file.split(",")[1], name: e.name }
                    })
                  : null,
              }),
              todoForTSR: true,
              ...(values.timeSlot && {
                calendarEvent: {
                  start: dateTimeFunction({ date: values.timeSlot[0], timeZone: true, apiRequest: true })?.toString(),
                  end: dateTimeFunction({ date: values.timeSlot[1], timeZone: true, apiRequest: true })?.toString(),
                },
              }),

              comments: values.comment,
              source: "web_v2",
              ...(values?.salesEvent && { salesEventId: values?.salesEvent }),
              ...(values?.meetingTime && { startTime: values?.meetingTime[0], endTime: values?.meetingTime[1] }),
              ...(values?.companyOffice && { agencyOfficeId: values?.companyOffice }),
              ...(values?.plusCode && { localityPlusCode: values?.plusCode }),
              ...(values?.visitedProject && { taskProjectId: values?.visitedProject }),
            }
          : {
              ...(values?.nextTask !== "do_nothing" && { nextStatus: values?.nextTask }),
              ...(values?.deadLine && {
                nextStatusDeadline: dateTimeFunction({
                  date: values?.timeSlot
                    ? dayjs(values?.timeSlot[0]).format(DATE_TIME_FORMAT)?.toString()
                    : dayjs(values?.deadLine).format(DATE_TIME_FORMAT),
                  timeZone: true,
                  apiRequest: true,
                }),
              }),
              ...(encodedFile && {
                attachments: encodedFile
                  ? encodedFile.map(e => {
                      return { file: e?.path ?? e?.file?.split(",")?.[1], name: e.name }
                    })
                  : null,
              }),
              comments: values?.comment,
              ...(values?.timeSlot && {
                calendarEvent: {
                  date: dateTimeFunction({ date: values?.dateSlot, timeZone: true, apiRequest: true })?.toString(),
                  start: dateTimeFunction({ date: values.timeSlot[0], timeZone: true, apiRequest: true })?.toString(),
                  end: dateTimeFunction({ date: values.timeSlot[1], timeZone: true, apiRequest: true })?.toString(),
                },
              }),
              ...(values?.newMeetingDate &&
                values?.newMeetingTime && {
                  calendarEvent: {
                    date: dateTimeFunction({
                      date: values?.newMeetingDate,
                      timeZone: true,
                      apiRequest: true,
                    })?.toString(),
                    start: dateTimeFunction({
                      date: values.newMeetingTime[0],
                      timeZone: true,
                      apiRequest: true,
                    })?.toString(),
                    end: dateTimeFunction({
                      date: values.newMeetingTime[1],
                      timeZone: true,
                      apiRequest: true,
                    })?.toString(),
                  },
                }),
              ...(values?.salesEvent && { salesEventId: values?.salesEvent }),
            }
        return { url: `/tasks/${taskId}`, method: "PUT", body: payload }
      },
      invalidatesTags: ["taskLogs", "tasksListing", "todos"],
    }),
    getEditedTaskDetails: build.query({
      query: ({ taskId, ...params }) => `/tasks/${taskId}/details?listingType=true&${convertQueryObjToString(params)}`,
      transformResponse: (response, _, arg) => {
        if (arg.detailsType === "todo") {
          return editedTodoLogTransformer(response)
        } else {
          return editedTaskLogTransformer(response)
        }
      },
    }),
    getTodosCount: build.query({
      query: ({ userId, params }) => {
        const dateR = params?.deadline?.split(",")
        params = {
          ...params,
          deadline: encodeURIComponent(`{"from":"${dateR[0]}","to":"${dateR[1]}"}`),
          userIds: userId,
        }
        return `/todos/user/stats?${convertQueryObjToString(params)}`
      },
      transformResponse: response => {
        const transformedData = getTodosCountTransformer(response)
        return transformedData
      },
    }),
    meetingVerification: build.mutation({
      query: payload => {
        const body = { ...payload, file: payload?.file?.split(",")[1] }
        return { url: "/attachments/validate", method: "POST", body }
      },
    }),
    getCompanyOffices: build.query({
      query: params => `/agencyOffices?${convertQueryObjToString(params)}`,
      transformResponse: resp =>
        resp?.map(e => ({ ...e, label: localisedValue({ object: e, key: "title" }), value: e?.id })),
    }),
    getOfficeCoordinates: build.query({
      query: plusCode => `/services/plusCodeCoordinates?plusCode=${encodeURIComponent(plusCode)}`,
    }),
    getGoogleMeetings: build.query({
      query: () => `/googleMeetMeetings?daysBack=7`,
      transformResponse: resp =>
        resp?.meetings?.map(e => ({
          label: e?.title,
          externalEventId: e?.calendarEventId,
          conferenceRecordName: e?.conferenceRecordName,
        })),
    }),
    validateGoogleMeeting: build.mutation({
      query: ({ conferenceRecordName, participants }) => ({
        url: `/googleMeetMeetings/validate`,
        method: "POST",
        body: {
          conferenceRecordName,
          ...(participants && { participants }),
        },
      }),
    }),
  }),
})

export const {
  useGetCellNumberQuery,
  useLazyGetCellNumberQuery,
  useAddTaskMutation,
  useCreateOtpMutation,
  useVerifyOtpMutation,
  useGetClientQuestionsQuery,
  useLazyGetClientQuestionsQuery,
  useGetClientProfileFilterOptionsQuery,
  useLazyGetClientProfileFilterOptionsQuery,
  useLazyGetClientAnswerQuery,
  useAddAnswersMutation,
  useGetTaskTypeCategoriesQuery,
  useGetTaskListingQuery,
  useGetTaskTypesQuery,
  useLazyGetTaskVirtualMeetingQuery,
  useLazyGetTaskVerificationStatusQuery,
  useUpdateTaskMutation,
  useLazyGetEditedTaskDetailsQuery,
  useGetTodosCountQuery,
  useLazyGetTodosCountQuery,
  useMeetingVerificationMutation,
  useGetCompanyOfficesQuery,
  useLazyGetCompanyOfficesQuery,
  useLazyGetGoogleMeetingsQuery,
  useLazyGetOfficeCoordinatesQuery,
  useValidateGoogleMeetingMutation,
} = taskFormApi
//
